import { useState, useEffect } from "react";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MenuItem,
  Card,
  Modal,
  Grid,
  TextField,
  Checkbox,
  ListItemText,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Encryption/Decryption
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";

function AddSpecilization({ isOpen, onRequestClose, id , fetchCoordinator }) {
  const [loading, setLoading] = useState(false);
  const [specializationList, setSpecializationList] = useState([]);
  const [counsilList, setCounsilList] = useState([]);

  const [customerDetails, setCustomerDetails] = useState({
    doctorid: id,
    specialization: [],
  });

  // ✅ Fetch specialization list
  const fetchSpecializations = async () => {
    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;
      if (!token) throw new Error("Authentication token not found.");

      const response = await axios.get("/api/specializationList", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        const decryptedData = decryptBody(response.data, secretKey);
        setSpecializationList(decryptedData || []);
      }
    } catch (error) {
      console.error("Error fetching specialists:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch counsil names
  const fetchCounsilNames = async () => {
    try {
      setLoading(true);
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;
      if (!token) throw new Error("Authentication token not found.");

      const response = await axios.get("/api/counsilNames", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        const decryptedData = decryptBody(response.data, secretKey);
        setCounsilList(decryptedData || []);
      }
    } catch (error) {
      console.error("Error fetching counsil names:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Load data on component mount
  useEffect(() => {
    fetchSpecializations();
    fetchCounsilNames();
  }, []);

  // ✅ Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const secretKey = await fetchSecretKey();
      const rawPayload = {
        specializations: customerDetails?.specialization?.map((s) => ({
          doctorspecialization: s.doctorspecialization,
          doctorid: id,
        })),
      };

      const encryptedData = encryptBody(rawPayload, secretKey);
      const token = JSON.parse(localStorage.getItem("loginData")).token;

      const response = await axios.post(
        "/api/doctorspecialization/create",
        encryptedData,
        {
          headers: {
            "Content-Type": "text/plain",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const decryptedResponse = decryptBody(response.data, secretKey);
        if (decryptedResponse?.success === true) {
          toast.success(
            decryptedResponse.message || "Specialization Added successfully!"
          );
          setCustomerDetails({ specialization: [] });
          onRequestClose();
          if (typeof fetchCoordinator === "function") {
            fetchCoordinator();
           }
        } else {
          toast.error(
            decryptedResponse?.message || "Failed to Add Specialization."
          );
        }
      }
    } catch (error) {
      console.error("Registration Error:", error);
      toast.error(error.response?.data || "Failed to Add Specialization");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Cancel button
  const handleCancel = () => {
    setCustomerDetails({ specialization: [] });
    onRequestClose();
  };

  return (
    <Modal open={isOpen} onClose={onRequestClose}>
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        p={2}
      >
        <Card
          sx={{
            width: { xs: "95%", sm: "90%", md: "600px" },
            padding: 3,
            margin: "auto",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <h5>Add Specialization</h5>
              </Grid>

              {/* ✅ Specializations dropdown */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Specializations"
                  fullWidth
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) =>
                      selected.length === 0
                        ? "Select Specializations"
                        : selected.join(", "),
                  }}
                  value={customerDetails.specialization.map(
                    (s) => s.doctorspecialization
                  )}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomerDetails({
                      ...customerDetails,
                      specialization: value.map((val) => ({
                        doctorspecialization: val,
                      })),
                    });
                  }}
                  sx={{ "& .MuiInputBase-root": { height: "45px" } }}
                >
                  {loading ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : specializationList.length > 0 ? (
                    specializationList.map((s) => (
                      <MenuItem key={s.id} value={s.specialization}>
                        <Checkbox
                          checked={customerDetails.specialization.some(
                            (sp) =>
                              sp.doctorspecialization === s.specialization
                          )}
                        />
                        <ListItemText primary={s.specialization} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No specializations available</MenuItem>
                  )}
                </TextField>
              </Grid>
            </Grid>

            {/* ✅ Buttons */}
            <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
               <MDButton
                variant="contained"
                type="submit"
                 sx={{
      background: "#2e7faeff !important",  // force override gradient bg
      color: "#fff !important",
      boxShadow: "none !important",
      "&:hover": {
        background: "#2e7faeff !important", // keep same color
        boxShadow: "none !important",
      },
      "&:focus": {
        background: "#2e7faeff !important",
        boxShadow: "none !important",
        outline: "none",
      },
      "&:active": {
        background: "#2e7faeff !important",
        boxShadow: "none !important",
      },
    }}
              >
                Submit
              </MDButton>
              <MDButton
                variant="outlined"
                onClick={handleCancel}
                 sx={{
      background: "#2e7faeff !important",  // force override gradient bg
      color: "#fff !important",
      boxShadow: "none !important",
      "&:hover": {
        background: "#2e7faeff !important", // keep same color
        boxShadow: "none !important",
      },
      "&:focus": {
        background: "#2e7faeff !important",
        boxShadow: "none !important",
        outline: "none",
      },
      "&:active": {
        background: "#2e7faeff !important",
        boxShadow: "none !important",
      },
    }}
              >
                Cancel
              </MDButton>
             
            </MDBox>
          </form>
        </Card>
      </MDBox>
    </Modal>
  );
}

export default AddSpecilization;
